import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, CheckCircle2, Home, Sparkles, Palette, Music, Languages, Users, Facebook, Instagram, Send } from "lucide-react";
import { api } from "@/lib/api";

const C = { navy: "#123B6D", teal: "#26A6A1", purple: "#7A3E9D", orange: "#F59C1A", yellow: "#F7C948", green: "#74B843" };

const T = {
  en: {
    heroTitle: "Join the Bilingual Exploradores Community!",
    heroP1: "We're building something special for children and families in our community.",
    heroP2: "Be among the first to hear about upcoming programs, cultural events, family experiences, and our new Exploradores Cultural Dance Collective.",
    tagline: "Explore. Belong. Grow.",
    cta: "Join the Interest List",
    pillars: [
      { Icon: Languages, label: "Language", color: C.navy },
      { Icon: Palette, label: "Creativity", color: C.purple },
      { Icon: Music, label: "Movement", color: C.orange },
      { Icon: Sparkles, label: "Culture", color: C.teal },
      { Icon: Users, label: "Community", color: C.green },
    ],
    formTitle: "Interest Form",
    parentSection: "Parent / Guardian Information",
    parentName: "Parent/Guardian Name", parentNameHint: "First & Last Name",
    email: "Email Address", phone: "Phone Number", city: "City",
    childSection: "Child Information",
    childName: "Child's Name", childAge: "Child's Age", optional: "(Optional)",
    addChildren: "Additional Children's Names & Ages", addChild: "Add Another Child",
    remove: "Remove", childNameShort: "Child's Name", age: "Age",
    interestsTitle: "What are you interested in?",
    interests: [
      { key: "Bilingual Exploradores", label: "Bilingual Exploradores", desc: "Family events, culture, creativity, language & community" },
      { key: "Exploradores Cultural Dance Collective", label: "Exploradores Cultural Dance Collective", desc: "Youth dance, movement, culture & confidence" },
      { key: "Both", label: "Both", desc: "Both of the above" },
    ],
    ageTitle: "What age group is your child?",
    aboutTitle: "Tell us a little about your family or what you'd love to see offered in our community.",
    consent: "I agree to receive updates about upcoming programs and events.",
    submit: "Join Our Community", submitting: "Joining...",
    errRequired: "Please complete all required fields, pick at least one interest, and agree to receive updates.",
    errEmail: "Please enter a valid email address.",
    errGeneric: "Something went wrong. Please try again.",
    successTitle: "Thank you for joining our interest list!",
    successBody: "We're excited to grow this community together and can't wait to share upcoming opportunities with your family. Follow us on Facebook and Instagram while we build something special.",
    returnHome: "Return to Home Page",
    switch: "Espa\u00F1ol",
  },
  es: {
    heroTitle: "\u00A1\u00DAnete a la Comunidad de Bilingual Exploradores!",
    heroP1: "Estamos creando algo especial para los ni\u00F1os y las familias de nuestra comunidad.",
    heroP2: "Sea de los primeros en recibir informaci\u00F3n sobre nuestros pr\u00F3ximos programas, eventos culturales, experiencias familiares y el nuevo Exploradores Cultural Dance Collective.",
    tagline: "Explora. Conecta. Crece.",
    cta: "\u00DAnete a la Lista de Inter\u00E9s",
    pillars: [
      { Icon: Languages, label: "Idioma", color: C.navy },
      { Icon: Palette, label: "Creatividad", color: C.purple },
      { Icon: Music, label: "Movimiento", color: C.orange },
      { Icon: Sparkles, label: "Cultura", color: C.teal },
      { Icon: Users, label: "Comunidad", color: C.green },
    ],
    formTitle: "Formulario de Inter\u00E9s",
    parentSection: "Informaci\u00F3n del Padre / Tutor",
    parentName: "Nombre del Padre/Tutor", parentNameHint: "Nombre y Apellido",
    email: "Correo Electr\u00F3nico", phone: "N\u00FAmero de Tel\u00E9fono", city: "Ciudad",
    childSection: "Informaci\u00F3n del Ni\u00F1o",
    childName: "Nombre del Ni\u00F1o", childAge: "Edad del Ni\u00F1o", optional: "(Opcional)",
    addChildren: "Nombres y Edades de Otros Ni\u00F1os", addChild: "Agregar Otro Ni\u00F1o",
    remove: "Quitar", childNameShort: "Nombre del Ni\u00F1o", age: "Edad",
    interestsTitle: "\u00BFQu\u00E9 le interesa?",
    interests: [
      { key: "Bilingual Exploradores", label: "Bilingual Exploradores", desc: "Eventos familiares, cultura, creatividad, idioma y comunidad" },
      { key: "Exploradores Cultural Dance Collective", label: "Exploradores Cultural Dance Collective", desc: "Danza juvenil, movimiento, cultura y confianza" },
      { key: "Both", label: "Ambos", desc: "Ambas opciones anteriores" },
    ],
    ageTitle: "\u00BFEn qu\u00E9 grupo de edad est\u00E1 su ni\u00F1o?",
    aboutTitle: "Cu\u00E9ntenos un poco sobre su familia o lo que le encantar\u00EDa ver en nuestra comunidad.",
    consent: "Acepto recibir informaci\u00F3n sobre pr\u00F3ximos programas y eventos.",
    submit: "\u00DAnete a Nuestra Comunidad", submitting: "Enviando...",
    errRequired: "Por favor complete los campos requeridos, elija al menos un inter\u00E9s y acepte recibir informaci\u00F3n.",
    errEmail: "Por favor ingrese un correo electr\u00F3nico v\u00E1lido.",
    errGeneric: "Algo sali\u00F3 mal. Int\u00E9ntelo de nuevo.",
    successTitle: "\u00A1Gracias por unirse a nuestra lista de inter\u00E9s!",
    successBody: "Estamos muy emocionados de crecer junto a nuestra comunidad y compartir muy pronto nuevas oportunidades para su familia. S\u00EDganos en Facebook e Instagram para acompa\u00F1arnos en este camino.",
    returnHome: "Volver a la P\u00E1gina Principal",
    switch: "English",
  },
};

const AGE_GROUPS = ["3\u20135", "6\u20138", "9\u201312", "13\u201317"];
const inputCls = "w-full bg-white rounded-2xl border-2 border-slate-200 px-4 py-3 outline-none transition focus:border-[#26A6A1]";
const lbl = "block text-sm font-semibold text-slate-700 mb-2";

export default function Exploradores() {
  const [lang, setLang] = useState("en");
  const tr = T[lang];
  useEffect(() => { document.title = "Bilingual Exploradores | Join Our Family Community"; }, []);

  const [d, setD] = useState({ parent_name: "", email: "", phone: "", city: "", child_name: "", child_age: "", about: "", consent: false });
  const [extra, setExtra] = useState([]);
  const [interests, setInterests] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [status, setStatus] = useState("idle");
  const [showErr, setShowErr] = useState(false);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email);
  const valid = d.parent_name && emailValid && d.phone && d.city && d.child_name && interests.length > 0 && d.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setShowErr(true); document.getElementById("explora-form").scrollIntoView({ behavior: "smooth" }); return; }
    setStatus("sending");
    try {
      await api.post("/exploradores", { ...d, additional_children: extra, interests, age_groups: ageGroups });
      setStatus("success"); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { setStatus("error"); }
  };

  if (status === "success") {
    return (
      <div className="min-h-screen grid place-items-center px-5 py-16 bg-white">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: "backOut" }}
          className="max-w-xl w-full rounded-3xl shadow-2xl p-8 md:p-12 text-center border-4" style={{ borderColor: C.yellow }} data-testid="explora-success">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
            className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-6 text-white" style={{ background: C.green }}><CheckCircle2 size={44} /></motion.div>
          <h1 className="text-2xl md:text-3xl font-extrabold mb-4" style={{ color: C.navy }}>{tr.successTitle}</h1>
          <p className="text-slate-600 leading-relaxed mb-6">{tr.successBody}</p>
          <div className="flex justify-center gap-3 mb-8">
            <span className="w-11 h-11 rounded-full grid place-items-center text-white" style={{ background: C.purple }}><Facebook size={20} /></span>
            <span className="w-11 h-11 rounded-full grid place-items-center text-white" style={{ background: C.orange }}><Instagram size={20} /></span>
          </div>
          <Link to="/" data-testid="explora-return-home" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-white" style={{ background: C.teal }}><Home size={18} /> {tr.returnHome}</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Lang toggle */}
      <div className="fixed top-4 right-4 z-50">
        <button onClick={() => setLang(lang === "en" ? "es" : "en")} data-testid="explora-lang" className="rounded-full px-4 py-2 text-sm font-bold text-white shadow-lg" style={{ background: C.navy }}>{tr.switch}</button>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.navy}, ${C.purple})` }}>
        <div className="max-w-4xl mx-auto px-5 py-20 text-center text-white relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block rounded-full px-4 py-1.5 text-sm font-bold mb-6" style={{ background: C.yellow, color: C.navy }}>Bilingual Exploradores</span>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-6">{tr.heroTitle}</h1>
            <p className="text-lg text-white/90 mb-3 max-w-2xl mx-auto">{tr.heroP1}</p>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">{tr.heroP2}</p>
            <p className="text-2xl font-bold mb-8" style={{ color: C.yellow }}>{tr.tagline}</p>
            <a href="#explora-form" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-white shadow-xl" style={{ background: C.orange }}>{tr.cta}</a>
          </motion.div>
          {/* pillars */}
          <div className="flex flex-wrap justify-center gap-4 mt-14">
            {tr.pillars.map(({ Icon, label, color }, i) => (
              <motion.div key={label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08 }}
                className="bg-white rounded-2xl px-5 py-4 flex flex-col items-center gap-2 shadow-lg w-28">
                <span className="w-11 h-11 rounded-xl grid place-items-center text-white" style={{ background: color }}><Icon size={22} /></span>
                <span className="text-xs font-bold" style={{ color: C.navy }}>{label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Create & Connect event banner */}
      <section className="max-w-2xl mx-auto px-5 mt-10">
        <Link to="/create-connect" data-testid="explora-event-banner"
          className="block rounded-3xl p-6 md:p-8 shadow-xl border-4 transition hover:-translate-y-0.5"
          style={{ borderColor: C.orange, background: "#FFF7EA" }}>
          <span className="inline-block rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-white mb-3" style={{ background: C.orange }}>
            {lang === "en" ? "Upcoming Event" : "Pr\u00F3ximo Evento"}
          </span>
          <h3 className="font-extrabold text-xl mb-1" style={{ color: C.navy }}>
            {lang === "en" ? "Create & Connect \u2014 Bilingual Hat Decorating Workshop" : "Crea y Conecta \u2014 Taller Biling\u00FCe de Decoraci\u00F3n de Gorras"}
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            {lang === "en"
              ? "Saturday, October 3 \u00B7 1:00\u20133:00 PM \u00B7 $25 per child \u00B7 Only 20 spots"
              : "S\u00E1bado, 3 de octubre \u00B7 1:00\u20133:00 PM \u00B7 $25 por ni\u00F1o(a) \u00B7 Solo 20 lugares"}
          </p>
          <span className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white" style={{ background: C.teal }}>
            {lang === "en" ? "Register Now" : "Inscr\u00EDbase Ahora"}
          </span>
        </Link>
      </section>

      {/* Form */}
      <section id="explora-form" className="max-w-2xl mx-auto px-5 py-14">
        <h2 className="text-3xl font-extrabold text-center mb-8" style={{ color: C.navy }}>{tr.formTitle}</h2>
        {showErr && !valid && <div className="mb-6 rounded-2xl border-2 border-red-200 bg-red-50 text-red-700 px-5 py-4 text-sm" data-testid="explora-error">{tr.errRequired}</div>}
        <form onSubmit={submit} noValidate className="space-y-8" data-testid="explora-form-el">
          {/* Parent */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6 md:p-8">
            <h3 className="font-bold text-lg mb-5" style={{ color: C.teal }}>{tr.parentSection}</h3>
            <div className="space-y-4">
              <div><label className={lbl}>{tr.parentName} * <span className="font-normal text-slate-400">{tr.parentNameHint}</span></label><input data-testid="explora-parent" className={inputCls} value={d.parent_name} onChange={(e) => setD({ ...d, parent_name: e.target.value })} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={lbl}>{tr.email} *</label><input type="email" data-testid="explora-email" className={inputCls} value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} />{showErr && !emailValid && <p className="text-xs text-red-500 mt-1">{tr.errEmail}</p>}</div>
                <div><label className={lbl}>{tr.phone} *</label><input type="tel" data-testid="explora-phone" className={inputCls} value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} /></div>
              </div>
              <div><label className={lbl}>{tr.city} *</label><input data-testid="explora-city" className={inputCls} value={d.city} onChange={(e) => setD({ ...d, city: e.target.value })} /></div>
            </div>
          </div>

          {/* Child */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6 md:p-8">
            <h3 className="font-bold text-lg mb-5" style={{ color: C.purple }}>{tr.childSection}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={lbl}>{tr.childName} *</label><input data-testid="explora-child" className={inputCls} value={d.child_name} onChange={(e) => setD({ ...d, child_name: e.target.value })} /></div>
              <div><label className={lbl}>{tr.childAge} <span className="font-normal text-slate-400">{tr.optional}</span></label><input type="number" min="1" data-testid="explora-child-age" className={inputCls} value={d.child_age} onChange={(e) => setD({ ...d, child_age: e.target.value })} /></div>
            </div>
            {extra.length > 0 && <p className="text-sm font-semibold text-slate-600 mt-5 mb-2">{tr.addChildren}</p>}
            {extra.map((c, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-3 items-end mb-3">
                <div><label className={lbl}>{tr.childNameShort}</label><input data-testid={`explora-extra-child-name-${i}`} className={inputCls} value={c.name} onChange={(e) => setExtra(extra.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></div>
                <div className="w-24"><label className={lbl}>{tr.age}</label><input data-testid={`explora-extra-child-age-${i}`} type="number" min="1" className={inputCls} value={c.age} onChange={(e) => setExtra(extra.map((x, j) => j === i ? { ...x, age: e.target.value } : x))} /></div>
                <button data-testid={`explora-remove-child-${i}`} aria-label={`${tr.remove} ${i + 1}`} type="button" onClick={() => setExtra(extra.filter((_, j) => j !== i))} className="text-red-500 pb-3"><Trash2 size={18} /></button>
              </div>
            ))}
            <button type="button" data-testid="explora-add-child" onClick={() => setExtra([...extra, { name: "", age: "" }])} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white mt-3" style={{ background: C.green }}><Plus size={16} /> {tr.addChild}</button>
          </div>

          {/* Interests */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6 md:p-8">
            <h3 className="font-bold text-lg mb-5" style={{ color: C.orange }}>{tr.interestsTitle}</h3>
            <div className="space-y-3">
              {tr.interests.map((it) => {
                const active = interests.includes(it.key);
                return (
                  <label key={it.key} className="flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition" style={{ borderColor: active ? C.teal : "#e2e8f0", background: active ? "#f0fdfa" : "#fff" }} data-testid={`explora-interest-${it.key}`}>
                    <input type="checkbox" checked={active} onChange={() => toggle(interests, setInterests, it.key)} className="mt-1 w-5 h-5 accent-[#26A6A1]" />
                    <span><span className="font-bold" style={{ color: C.navy }}>{it.label}</span>{it.desc && <span className="block text-sm text-slate-500">{it.desc}</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Age group */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6 md:p-8">
            <h3 className="font-bold text-lg mb-5" style={{ color: C.green }}>{tr.ageTitle}</h3>
            <div className="flex flex-wrap gap-3">
              {AGE_GROUPS.map((g) => {
                const active = ageGroups.includes(g);
                return <button type="button" data-testid={`explora-age-${g}`} key={g} onClick={() => toggle(ageGroups, setAgeGroups, g)} className="px-5 py-2.5 rounded-full text-sm font-bold border-2 transition" style={active ? { background: C.yellow, borderColor: C.yellow, color: C.navy } : { borderColor: "#e2e8f0", color: "#475569" }}>{g}</button>;
              })}
            </div>
          </div>

          {/* About */}
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6 md:p-8">
            <label className="font-bold text-lg block mb-4" style={{ color: C.navy }}>{tr.aboutTitle}</label>
            <textarea data-testid="explora-about" rows={4} className={inputCls} value={d.about} onChange={(e) => setD({ ...d, about: e.target.value })} />
          </div>

          {/* Consent + submit */}
          <label className="flex items-start gap-3 cursor-pointer" data-testid="explora-consent">
            <input type="checkbox" checked={d.consent} onChange={(e) => setD({ ...d, consent: e.target.checked })} className="mt-1 w-5 h-5 accent-[#7A3E9D]" />
            <span className="text-sm font-medium text-slate-700">{tr.consent} *</span>
          </label>
          {status === "error" && <p className="text-red-500 text-center">{tr.errGeneric}</p>}
          <button type="submit" data-testid="explora-submit" disabled={status === "sending"} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-5 font-extrabold text-white text-lg shadow-xl disabled:opacity-60" style={{ background: C.orange }}>
            <Send size={20} /> {status === "sending" ? tr.submitting : tr.submit}
          </button>
        </form>
      </section>
    </div>
  );
}
